import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { Mail, Lock, LogIn, ArrowRight, Loader2, ShieldCheck, Key } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  
  // 2FA states
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [userId, setUserId] = useState(null);
  
  const { login, verify2FA } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!email || !password) {
      setError(t('auth.fill_all_fields'));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await login({ email, password });
      if (result.requires_verification) {
        navigate("/verify-email", { state: { email } });
        return;
      }
      if (result.mfa_required) {
        setShow2FA(true);
        setUserId(result.userId);
        return;
      }
      if (result.success) {
        navigate("/dashboard");
      } else {
        setError(result.error || t('auth.invalid_credentials'));
      }
    } catch (err) {
      setError(t('auth.tech_error'));
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async () => {
    if (!twoFactorCode || twoFactorCode.length < 6) {
      setError(t('auth.enter_6_digit') || "Veuillez entrer le code à 6 chiffres");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const result = await verify2FA(userId, twoFactorCode);
      if (result.success) {
        navigate("/dashboard");
      } else {
        setError(result.error || t('auth.invalid_2fa_code') || "Code 2FA invalide");
      }
    } catch (err) {
      setError(t('auth.tech_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (show2FA) {
        handle2FASubmit();
      } else {
        handleLogin();
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden px-3 sm:px-4 font-inter">
      {/* Abstract Background Effects - responsive sizing */}
      <div className="absolute top-0 left-1/4 w-[300px] sm:w-full max-w-[500px] lg:w-full max-w-[600px] h-[300px] sm:h-[500px] lg:h-[600px] bg-indigo-500/10 rounded-full blur-[100px] sm:blur-[140px] pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-[300px] sm:w-full max-w-[500px] lg:w-full max-w-[600px] h-[300px] sm:h-[500px] lg:h-[600px] bg-cyan-500/10 rounded-full blur-[100px] sm:blur-[140px] pointer-events-none"></div>

      <div className="w-full max-w-full max-w-[480px] z-10 animate-fade-in px-0 sm:px-4">
        {/* Branding */}
        <div className="flex flex-col items-center mb-6 sm:mb-10">
          <div className="relative group mb-4 sm:mb-6">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-2xl blur opacity-30 group-hover:opacity-60 transition duration-1000"></div>
            <div className="relative bg-slate-900 border border-white/10 rounded-2xl p-3 sm:p-4 shadow-2xl">
              <span className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent tracking-tighter uppercase">
                Ennovatrade
              </span>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-white font-outfit mb-2 tracking-tight text-center">
            {show2FA ? t('auth.two_factor_auth') || "Verification 2FA" : t('auth.pro_access')}
          </h1>
          <p className="text-sm sm:text-base text-slate-400 font-medium text-center max-w-full max-w-[320px] px-2">
            {show2FA ? "Entrez le code d'authentification a 6 chiffres" : t('auth.connect_terminal')}
          </p>
        </div>

        {/* Login Card */}
        <div className="trading-card p-5 sm:p-8 lg:p-10 border-white/10 shadow-2xl relative mx-2 sm:mx-0">
          {error && (
            <div className="mb-4 sm:mb-8 p-3 sm:p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-2 sm:gap-3 text-rose-400 animate-fade-in font-semibold text-xs sm:text-sm">
              <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-rose-500 flex-shrink-0"></div>
              <span className="break-words">{error}</span>
            </div>
          )}

          {!show2FA ? (
            <div className="space-y-4 sm:space-y-6 animate-fade-in">
              {/* Email Field */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-[10px] sm:text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.email_corp')}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                    <Mail size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </div>
                  <input 
                    type="email" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('auth.email_placeholder')}
                    className="w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-3 sm:py-4 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/40 transition-all font-medium text-sm"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-1.5 sm:space-y-2">
                <div className="flex items-center justify-between ml-1">
                  <label className="text-[10px] sm:text-xs font-black text-slate-500 uppercase tracking-widest">{t('auth.access_key')}</label>
                  <Link to="/forgot-password" className="text-[10px] sm:text-xs font-bold text-indigo-400 hover:text-indigo-300">{t('auth.forgot_password')}</Link>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                    <Lock size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </div>
                  <input 
                    type="password" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="••••••••••••"
                    className="w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-3 sm:py-4 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/40 transition-all font-medium text-sm"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <button 
                onClick={handleLogin} 
                disabled={loading}
                className="btn-premium w-full mt-2 sm:mt-4 !py-3 sm:!py-4 text-white uppercase tracking-widest font-black text-xs sm:text-sm flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    {t('auth.enter_terminal')}
                    <ArrowRight size={16} className="sm:w-[18px] sm:h-[18px] transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4 sm:space-y-6 animate-fade-in">
              {/* 2FA Field */}
              <div className="space-y-1.5 sm:space-y-2">
                <label className="text-[10px] sm:text-xs font-black text-slate-500 uppercase tracking-widest ml-1">{t('auth.two_factor_code') || "Code 2FA"}</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-indigo-400 transition-colors">
                    <Key size={16} className="sm:w-[18px] sm:h-[18px]" />
                  </div>
                  <input 
                    type="text"
                    maxLength="6"
                    value={twoFactorCode} 
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyPress={handleKeyPress}
                    placeholder="000000"
                    className="w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-3 sm:py-4 rounded-xl bg-slate-950/50 border border-white/5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500/40 transition-all font-medium text-center text-lg sm:text-xl tracking-[0.3em]"
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>

              {/* Verify Button */}
              <button 
                onClick={handle2FASubmit} 
                disabled={loading || twoFactorCode.length < 6}
                className="btn-premium w-full mt-2 sm:mt-4 !py-3 sm:!py-4 text-white uppercase tracking-widest font-black text-xs sm:text-sm flex items-center justify-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    {t('auth.verify_and_connect') || "Verifier et Connecter"}
                    <ArrowRight size={16} className="sm:w-[18px] sm:h-[18px] transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>

              <button
                onClick={() => setShow2FA(false)}
                className="w-full mt-2 sm:mt-4 text-slate-400 hover:text-white transition-colors text-xs sm:text-sm font-medium"
              >
                {t('auth.cancel_back') || "Annuler et retourner au login"}
              </button>
            </div>
          )}

          <div className="mt-6 sm:mt-10 pt-4 sm:pt-5 border-t border-white/5 flex flex-col items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 bg-white/5 rounded-lg border border-white/5">
              <ShieldCheck size={12} className="sm:w-[14px] sm:h-[14px] text-emerald-500" />
              <span className="text-[8px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('auth.ssl_secure')}</span>
            </div>
            
            {!show2FA && (
              <p className="text-xs sm:text-sm font-bold text-slate-500 text-center">
                {t('auth.new_to_platform')}{" "}
                <button 
                  onClick={() => navigate("/register")} 
                  className="text-indigo-400 hover:text-indigo-300 transition-colors"
                  disabled={loading}
                >
                  {t('auth.create_trader_profile')}
                </button>
              </p>
            )}
          </div>
        </div>
        
        {/* Verification Seals */}
        <div className="mt-6 sm:mt-8 flex justify-center items-center gap-4 sm:gap-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-500 flex-wrap">
            <div className="text-[8px] sm:text-[10px] font-black tracking-tighter text-slate-400 flex items-center gap-1">
                <LogIn size={10} className="sm:w-[12px] sm:h-[12px]" /> {t('auth.secure_connect')}
            </div>
            <div className="text-[8px] sm:text-[10px] font-black tracking-tighter text-slate-400">{t('auth.data_by')}</div>
            <div className="text-[8px] sm:text-[10px] font-black tracking-tighter text-slate-400">{t('auth.encryption')}</div>
        </div>
      </div>
    </div>
  );
}